import { z } from 'zod';
import { PointOrPolygonSchema } from './geojson';
import { OwnedIncidentRefSchema } from './high-risk-hotspot';
import { IncidentLifecycleStateSchema } from './incident';

/**
 * Active wildland fire feature (Phase E.2.1) — the first operator-visible Fire
 * Risk layer. Backed by the `active_wildland_fires` table (CWFIS national +
 * provincial feeds, source-tagged) with a query-time perimeter join to
 * `fire_perimeters`.
 *
 * Geometry is polymorphic: a perimeter (Multi)Polygon when a matching
 * fire_perimeters row was found within range + time, otherwise the incident
 * Point. `centroid` always carries the incident point [lon, lat] so the side
 * panel can show coordinates and fly-to works regardless of render geometry.
 */

/** The canonical CWFIS stage-of-control vocabulary (E.1.7 normalization). A
 *  value outside this set (or null) renders grey with a client-side WARN. */
export const FIRE_STAGES = ['OC', 'BH', 'UC', 'OUT'] as const;
export type FireStage = (typeof FIRE_STAGES)[number];

/** Human-readable labels for the stage badges. */
export const FIRE_STAGE_LABELS: Record<FireStage, string> = {
  OC: 'Out of Control',
  BH: 'Being Held',
  UC: 'Under Control',
  OUT: 'Extinguished',
};

export function isFireStage(v: string | null | undefined): v is FireStage {
  return v != null && (FIRE_STAGES as readonly string[]).includes(v);
}

/** The community an OC-fire urgent alert threatens (#444). Names WHO is at risk so
 *  the municipal seat can act — distance + 8-point compass bearing (community→fire). */
export const ThreatenedCommunitySchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  population: z.number().int().nullable(),
  /** Great-circle metres from the community centroid to the fire. */
  distance_m: z.number().nullable(),
  /** Bearing community→fire, degrees 0–360 (0 = N, clockwise). */
  bearing_deg: z.number().nullable(),
  /** 8-point compass label of bearing_deg (e.g. 'SW'); null when bearing absent. */
  compass: z.string().nullable(),
  /** #632 Community Egress Slice A — the community's surface access mode
   *  (road | fly_in | winter_road | water | rail | unknown). A FACT: a fly-in community
   *  (no road out) is categorically more exposed to a nearby fire. Never null (DB default
   *  'unknown'); older payloads that omit it default to 'unknown'. */
  access_type: z.string().default('unknown'),
  /** ICAO / Transport Canada LID of the community's primary airstrip (e.g. 'CZSJ' for
   *  Sandy Lake); null when none / unconfirmed. IDENTIFIER only — observed condition is Slice B. */
  primary_aerodrome_ident: z.string().nullable().default(null),
  primary_aerodrome_name: z.string().nullable().default(null),
  /** §G Wave 3 (#647) — the DOWNWIND FACT (three-state): is this community in the fire's
   *  wind-transport path (±45°)?
   *    true  — wind KNOWN/fresh AND the community is INSIDE the cone (a FACT).
   *    false — wind KNOWN/fresh AND the community is OUTSIDE the cone (a FACT).
   *    null  — UNKNOWN: no wind persisted, a STALE wind, a CALM wind, or no bearing.
   *  UNKNOWN is NEVER "not downwind". It is a FACT ON THE PANEL, NEVER a score/RL input (the
   *  RL was earned omnidirectionally by distance). `.optional()` (no default): an older payload
   *  that omits it is `undefined` (field-absent), distinct from a computed `null` (known-unknown). */
  downwind: z.boolean().nullable().optional(),
  /** The fresh wind the downwind fact was computed from (persisted by the fusion pass into
   *  escalation_breakdown.wind), or null when unknown/stale. Rendered fact-only, with its own age. */
  wind: z
    .object({
      speed_kmh: z.number(),
      direction_deg: z.number(),
      observed_at: z.string(),
    })
    .nullable()
    .optional(),
});
export type ThreatenedCommunity = z.infer<typeof ThreatenedCommunitySchema>;

/**
 * #635 — the FACT-ONLY access-mode label for a community, or null for a road-accessible
 *  community (nothing to add). Used on the operator FIRE threat surface, e.g.
 *  "Sandy Lake — Access: fly-in · 18 km NE". 'unknown' is NEVER rendered as reassuring.
 *
 *  FOUNDER RULING (Christopher, 2026-07-13, docs/fire-scorer-rebuild-g-gate-2026-07-13.md §3.2):
 *  the operator string is "Access: fly-in" — NOT "fly-in, no road out". "no road out" is a
 *  CONCLUSION the COP is not accountable for (access_type = fly_in means "no all-season road in
 *  our dataset"; it does not establish there is no ice road or marine egress). Same logic that
 *  retired "Egress: open". Applied to every mode — state the FACT, never the egress conclusion
 *  ("no road out" / "no road bridge" / "no road" / "only" all struck). See the §4.3 registry line.
 */
export function accessTypeLabel(accessType: string | null | undefined): string | null {
  switch (accessType) {
    case 'fly_in':
      return 'Access: fly-in';
    case 'winter_road':
      return 'Access: winter road';
    case 'water':
      return 'Access: water';
    case 'rail':
      return 'Access: rail';
    case 'unknown':
      return 'access unconfirmed';
    case 'road':
    default:
      return null;
  }
}

export const ActiveFireFeaturePropertiesSchema = z.object({
  id: z.string().uuid(),
  /** Agency-local fire id (e.g. 'THU_FIRE_018'); may be null. */
  fire_id: z.string().nullable(),
  /** Feed discriminator: 'cwfis-active-fires' | 'bc-wildfire-service' | … */
  source: z.string(),
  /** Reporting agency / province code (e.g. 'ON'); may be null. */
  agency: z.string().nullable(),
  /** Verbatim stage-of-control; canonical OC/BH/UC/OUT or null (unmapped). */
  stage_of_control_status: z.string().nullable(),
  /** CWFIS response_type promoted from `attrs` (FUL = full response, MOD/MON =
   *  monitored, null when absent). L.2.1: Default Layer 4 ("Reported OC
   *  Wildfires") is `stage = OC AND response_type = FUL` — the red flame; an OC
   *  fire whose response_type ≠ FUL is "OC Monitored" (orange dot). */
  response_type: z.string().nullable(),
  /** #618 Fix 1b — the RAW upstream vocabulary the province ACTUALLY filed (province-preferred
   *  across the merged same-fire group), so the panel can show what the source said under the
   *  derived CIFFC label: e.g. Ontario "Being Observed" (BM) + a raw "MNP" behind a 'UNK' /
   *  "response not reported" posture. null for a CWFIS-only fire (the national feed carries no
   *  upstream_*_raw). Optional so detail-constructed / pre-existing fixtures stay valid. */
  upstream_stage_raw: z.string().nullable().optional(),
  upstream_response_type_raw: z.string().nullable().optional(),
  hectares_estimated: z.number().nullable(),
  observed_at: z.string(),
  last_updated_at: z.string().nullable(),
  /** True when a perimeter polygon was matched (geometry is that polygon). */
  has_perimeter: z.boolean(),
  /** Perimeter area in km² (ST_Area of the matched polygon); null when none. */
  perimeter_area_km2: z.number().nullable(),
  /** Incident point [lon, lat] — always present, even when geometry is a
   *  polygon — for the side-panel coordinates and click-to-fly. */
  centroid: z.tuple([z.number(), z.number()]),
  /** IMS Response Level (1–4) of the nearest fusion-flagged high-risk hotspot
   *  (within 5 km + 6 h), if any. null when no linked hotspot — most reported
   *  fires won't have one. Surfaced as a badge in the side panel (IMS.7). */
  response_level: z.number().int().min(1).max(4).nullable(),
  /** Track B (#441) — reported-fire ESCALATION INDEX (0–100): a forward-looking
   *  fire-RISK / OC-risk estimate (which not-yet-OC reported fires are about to run
   *  and are worth scarce drone/aircraft monitoring) from weather + fuel +
   *  wind-toward-values + downwind spotting + growth + proximity. Distinct from
   *  response_level (a backward corroboration read). null = not yet scored (OC/OUT
   *  are out of scope, or no pass has run). Honest estimate — never a fire stage. */
  escalation_index: z.number().int().min(0).max(100).nullable().optional(),
  /** Explainable escalation breakdown: per-term sub-scores + flags + a one-line
   *  operator narrative ("ISI 17 · C-3 crown fuel · wind→village ±8° 12 km · 3
   *  spot-hotspots 1.8 km NE · crossover"). See fusion/reported-fire-escalation.ts. */
  escalation_breakdown: z.record(z.unknown()).nullable().optional(),
  /** When the escalation index was last computed (UTC ISO), for staleness. */
  escalation_updated_at: z.string().nullable().optional(),
  /** Full upstream attribute tail + provenance. */
  attrs: z.record(z.unknown()).nullable(),
  /** Coordination re-arch Phase 5b — the tracked incident this fire was Declared
   *  into, if any (the fire↔incident thread). REUSES the OwnedIncidentRef shape
   *  from high-risk-hotspot. Drives the uniform managed ring on any owned wildfire
   *  marker (founder ruling 2026-06-22 — ownership is the ring, not a per-stage
   *  symbol) + the FireBody owned badge. Optional + nullable so detail-constructed /
   *  pre-existing-test features stay valid; null when the fire is not currently an
   *  owned (non-closed) incident. */
  owned_incident: OwnedIncidentRefSchema.nullable().optional(),
  /** R1/#477 — the owning incident's lifecycle phase. DRIVES the marker transform:
   *  'response' shows the ACK-STEEL ring (re-sourced from owned_incident), 'deactivated'
   *  SUPPRESSES the marker. Surfaced even when deactivated/closed (unlike owned_incident,
   *  which stays open-only). null when the fire is not a tracked incident's source. */
  incident_lifecycle_state: IncidentLifecycleStateSchema.nullable().optional(),
  // ── OC-fire urgent alert (#444 — founder alert SOURCE 2, §A.1) ──
  /** The RISK-GATED urgent Response Level for an OC fire (the anti-flood ladder:
   *  OC floor RL2 + values/size/CAP/escalation escalators — oc-fire-rl.ts). 2..4;
   *  null for any non-OC fire. RL≥3 surfaces on the red urgent queue + the map
   *  urgent pulse; RL2 stays muted (drawn as a glyph, not red). Distinct from
   *  `response_level` (the co-located-hotspot corroboration read). */
  oc_fire_response_level: z.number().int().min(1).max(4).nullable().optional(),
  // ── #445-ws2 — the owning incident's OPERATOR-ASSESSED Response Level overlay ──
  /** The IC's operator-assessed Response Level on the owning (non-closed) incident, or null
   *  when not owned / no pin. The map disc + oc_fire queue read the ESCALATION-SAFE max of
   *  this and `oc_fire_response_level` (escalationSafeResponseLevel): an IC escalation lifts
   *  them (COALESCE); an IC de-escalation can never bury a worse derived (the guard). The
   *  derived `oc_fire_response_level` is NEVER mutated. */
  operator_response_level: z.number().int().min(1).max(4).nullable().optional(),
  /** The assessing Incident Commander (command_officer_name snapshot) — the "assessed by
   *  <IC>" honesty annotation on the fire's OC-alert panel. null when no pin. */
  operator_rl_by: z.string().nullable().optional(),
  /** The IC's assessment reason for the pin, or null. */
  operator_rl_reason: z.string().nullable().optional(),
  /** When the IC's assessment landed (ISO), or null. */
  operator_rl_at: z.string().nullable().optional(),
  /** The named community the OC alert threatens — what the municipal seat acts on
   *  ("OC · RL3 · 6 km SW of <name>"). The nearest non-retired community within
   *  25 km; null when none in reach. bearing/compass is community→fire (the fire's
   *  position relative to the community).
   *
   *  #667 — this is the NEAREST element of {@link threatened_communities} (the SET), kept as
   *  a backward-compat alias so every existing consumer reads the nearest byte-identically.
   *  Prefer `threatened_communities` when you need every in-radius community. */
  threatened_community: ThreatenedCommunitySchema.nullable().optional(),
  /** #667 — the SET of EVERY non-retired community within the fire's posture-aware effective
   *  radius (fought 25 km / unfought 50 km), nearest-first, each carrying the SAME honest facts as
   *  {@link threatened_community} (name · population · distance · bearing · compass · access ·
   *  aerodrome · the three-state wind-path FACT + the wind it was computed from). The fix for the
   *  SINGULAR-payload life-safety bug: a fire in the wind path of a SECOND community used to raise
   *  NOTHING for it. RAISE-ONLY — element [0] equals `threatened_community` (the nearest); the set
   *  only ever ADDS the 2nd, 3rd, … the singular dropped. Each element's `downwind` is the honest
   *  three-state FACT (true/false/null — UNKNOWN is never "not in path"), NEVER a score/RL input.
   *  `.optional()` (no default): an older payload / detail-constructed fixture that omits it is
   *  `undefined`; consumers treat that as an empty set. */
  threatened_communities: z.array(ThreatenedCommunitySchema).optional(),
  /** True when a Severe/Extreme (canonical CAP RL≥3) alert overlaps the fire — an
   *  RL4 escalator. */
  severe_cap_proximate: z.boolean().optional(),
  /** The overlapping severe CAP's event name (e.g. 'Wildfire'), for the alert card. */
  severe_cap_event: z.string().nullable().optional(),
  // #447 Track A Slice 3b RETIRED the co_located_hotspot_id queue-fold stopgap (#444
  // blocker #8): the hotspot-side suppression now happens AT SOURCE (a hotspot that is a
  // known fire's downwind spread is demoted off urgent in fusion), so the client no longer
  // folds at render. The OC-fire alert + its RL ladder are unchanged.
  /** Unified anomaly workstate (source_type='oc_fire') — the ack lifecycle. null
   *  work_acknowledged_at = unacked (the urgent pulse is RED + pulsing); a timestamp
   *  = acked (recedes to ACK-STEEL, static). Routed through anomalyMarker.isAcknowledged. */
  work_acknowledged_at: z.string().nullable().optional(),
  micro_status: z.enum(['new', 'working', 'resolving']).optional(),
  owner_label: z.string().nullable().optional(),
  /** #603 freshness-on-merge — the distinct feeds contributing to this merged
   *  fire, canonical (CWFIS) first. A same-fire CWFIS+provincial pair collapses to
   *  ONE feature listing both, so provenance reads "CWFIS · also BC Wildfire
   *  Service". Optional (detail-constructed / pre-existing test features may omit
   *  it); falls back to [this.source]. */
  sources: z.array(z.string()).nullable().optional(),
});
export type ActiveFireFeatureProperties = z.infer<typeof ActiveFireFeaturePropertiesSchema>;

export const ActiveFireFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: PointOrPolygonSchema,
  properties: ActiveFireFeaturePropertiesSchema,
});
export type ActiveFireFeature = z.infer<typeof ActiveFireFeatureSchema>;

export const ActiveFireFeatureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(ActiveFireFeatureSchema),
});
export type ActiveFireFeatureCollection = z.infer<typeof ActiveFireFeatureCollectionSchema>;
